import axios from "axios";
import setAuthToken, { applyAuthToken, clearAuthToken } from "./token";
import {
    createAuthEventId,
    recentExpectedAuthExitReason,
    reportUnexpectedAuthRedirect,
} from "./auth-redirect-reporter";

/**
 * Silent access-token refresh on 401 (OWASP API2 remediation #3).
 *
 * When any request comes back 401, we POST to the refresh endpoint once. The httpOnly
 * refresh cookie is sent automatically (same-origin); the server rotates it and returns
 * a fresh short-lived access token, which we store and use to retry the original request.
 * Concurrent 401s share a single in-flight refresh. If the refresh itself fails, the
 * session is over and we bounce to login.
 */

// Dedicated client with NO interceptors, so the refresh call can never recurse.
const refreshClient = axios.create();

let refreshPromise = null;
let authSyncInstalled = false;

const FAILURE_TTL_MS = 10000;

function getRefreshUrl() {
    // Injected per-page by the Blade layout. Absent on site-less pages, where there is
    // no session to refresh — in that case we skip refreshing rather than guess a URL.
    return window.bo_refresh_token_url || null;
}

function tokenKey() {
    return "token_" + window.bo_site;
}

function refreshFailureKey() {
    return "bo_refresh_failure_" + window.bo_site;
}

function readStoredToken() {
    try {
        return localStorage.getItem(tokenKey());
    } catch (e) {
        return null;
    }
}

function installCrossTabTokenSync() {
    if (authSyncInstalled || typeof window === "undefined") {
        return;
    }
    authSyncInstalled = true;

    window.addEventListener("storage", (event) => {
        if (event.key === tokenKey() && event.newValue) {
            // Updating headers only avoids echoing the same storage event between tabs.
            applyAuthToken(event.newValue);
        }
    });
}

function requestToken(config) {
    const headers = (config && config.headers) || {};
    const authorization = headers.Authorization || headers.authorization || "";
    if (typeof authorization === "string" && authorization.indexOf("Bearer ") === 0) {
        return authorization.slice(7);
    }
    return headers["x-auth-token"] || null;
}

function refreshErrorDetails(error) {
    return error && error.response && error.response.data && error.response.data.errors
        ? error.response.data.errors
        : {};
}

function retryWithToken(instance, config, token) {
    applyAuthToken(token);
    config._retriedAfterRefresh = true;
    config.headers = config.headers || {};
    config.headers["Authorization"] = "Bearer " + token;
    config.headers["x-auth-token"] = token;
    return instance(config);
}

/**
 * Only a 401 from the refresh endpoint proves that the refresh session is no longer valid.
 * Rate limiting, server errors, and network failures are transient and must not clear the
 * shared token or send every open tab to login.
 */
function isDefinitiveRefreshFailure(error) {
    return !!(error && error.response && error.response.status === 401);
}

function recentSharedFailure() {
    try {
        const value = JSON.parse(localStorage.getItem(refreshFailureKey()) || "null");
        if (!value || Date.now() - Number(value.at || 0) > FAILURE_TTL_MS) {
            return null;
        }
        const error = new Error("Refresh failed in another browser tab");
        if (value.status) {
            error.response = {
                status: value.status,
                data: {
                    errors: {
                        reason_code: value.reason_code || null,
                        event_id: value.event_id || null,
                    },
                },
            };
        }
        error.boAuthEventId = value.event_id || null;
        return error;
    } catch (e) {
        return null;
    }
}

function rememberSharedFailure(error) {
    try {
        const errors = refreshErrorDetails(error);
        localStorage.setItem(
            refreshFailureKey(),
            JSON.stringify({
                at: Date.now(),
                status: error && error.response ? error.response.status || null : null,
                reason_code: errors.reason_code || null,
                event_id: errors.event_id || (error && error.boAuthEventId) || null,
            })
        );
    } catch (e) {}
}

function clearSharedFailure() {
    try {
        localStorage.removeItem(refreshFailureKey());
    } catch (e) {}
}

function performRefresh() {
    const eventId = createAuthEventId();
    const expectedExitReason = recentExpectedAuthExitReason();
    const headers = { "X-BO-Auth-Event-ID": eventId };
    if (expectedExitReason) {
        headers["X-BO-Expected-Auth-Exit"] = expectedExitReason;
    }

    return refreshClient
        .post(getRefreshUrl(), {}, { withCredentials: true, headers })
        .then((res) => {
            const token = res.data.access_token;
            clearSharedFailure();
            setAuthToken(token);
            return token;
        })
        .catch((error) => {
            const errors = refreshErrorDetails(error);
            error.boAuthEventId = errors.event_id || eventId;
            rememberSharedFailure(error);
            return Promise.reject(error);
        });
}

function refreshedByAnotherTab(observedToken) {
    const latest = readStoredToken();
    if (latest && observedToken && latest !== observedToken) {
        applyAuthToken(latest);
        return latest;
    }
    return null;
}

function coordinatedWithWebLock(observedToken) {
    const lockName = "boostorder-refresh-" + window.bo_site;
    return navigator.locks.request(lockName, { mode: "exclusive" }, () => {
        const sharedToken = refreshedByAnotherTab(observedToken);
        if (sharedToken) {
            return sharedToken;
        }
        const sharedFailure = recentSharedFailure();
        return sharedFailure ? Promise.reject(sharedFailure) : performRefresh();
    });
}

function coordinatedRefresh(observedToken) {
    if (
        typeof navigator !== "undefined" &&
        navigator.locks &&
        typeof navigator.locks.request === "function"
    ) {
        return coordinatedWithWebLock(observedToken);
    }
    // Older browsers still benefit from token propagation and the server's 30-second
    // concurrent-rotation grace, even though they cannot elect one refresh leader.
    return performRefresh();
}

function isCustomerPortal() {
    // The consumer (customer-portal) Blade layouts inject window.bo_require_login; the admin
    // layout does not. Its presence is our portal signal: defined → customer portal.
    return typeof window.bo_require_login !== "undefined";
}

function getLoginUrl() {
    // The login route shares the refresh route's base + site prefix (…/{site}/refresh-token).
    // Send the user to the login page of the portal they're actually in:
    //   customer portal → …/{site}/customer-login
    //   admin portal    → …/{site}/login
    // (Deriving a single /login for both was why customer-portal redirects wrongly landed on
    // the admin login page.)
    const refreshUrl = getRefreshUrl();
    if (!refreshUrl) {
        return null;
    }
    const loginPath = isCustomerPortal() ? "/customer-login" : "/login";
    return refreshUrl.replace(/\/refresh-token(\?.*)?$/, loginPath);
}

function isLoggedIn() {
    // The refresh token itself is httpOnly (unreadable), so use the stored access token as
    // the "there is a session here" signal. Present → try to refresh; absent → go straight
    // to login instead of attempting a refresh that can't succeed.
    try {
        return !!localStorage.getItem("token_" + window.bo_site);
    } catch (e) {
        return false;
    }
}

function isOnLoginPage() {
    // Structural loop guard: we must never "redirect" the login page to itself (that reload
    // was the original loop). Matches any route ending in "login" — the admin/company login
    // (…/login) AND the consumer login (…/customer-login). Missing the latter defeated the
    // guard and let two login tabs ping-pong on redirect.
    try {
        return /login\/?$/i.test(window.location.pathname);
    } catch (e) {
        return false;
    }
}

function doRefresh(observedToken = readStoredToken()) {
    if (!refreshPromise) {
        refreshPromise = coordinatedRefresh(observedToken).finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

function redirectToLogin() {
    clearAuthToken();

    // Already on the login page → do nothing. This is the loop guard: a 401 from a login-page
    // XHR must not reload login. Any OTHER page navigates once to a DIFFERENT url (login), so
    // it can't loop either.
    if (isOnLoginPage()) {
        return;
    }

    const loginUrl = getLoginUrl();
    if (!loginUrl) {
        return;
    }
    // Hard loop breaker: never navigate to the URL we're already on (that is a reload,
    // and a reloading login page was the reported symptom).
    try {
        if (new URL(loginUrl, window.location.origin).pathname === window.location.pathname) {
            return;
        }
    } catch (e) {}
    window.location.assign(loginUrl);
}

/**
 * Replay a request with no credentials at all.
 *
 * Used on guest-browsing sites once a session is definitively gone: the guest-tolerant endpoints
 * can still answer, so stripping the dead token and retrying yields the correct guest response
 * instead of an error the visitor can do nothing about. `_retriedAfterRefresh` is already set by
 * the caller, so this can never loop.
 */
function retryAsGuest(instance, config, err) {
    clearAuthToken();
    if (config.headers) {
        delete config.headers["Authorization"];
        delete config.headers["x-auth-token"];
    }
    return instance(config).catch(() => Promise.reject(err));
}

/**
 * Proactively refresh the access token (used by keep-alive heartbeats on iframe pages
 * that otherwise make no requests). Shares the same single-flight refresh as the 401
 * interceptor. No-ops on pages without a refresh endpoint configured.
 */
export function refreshAccessToken() {
    if (!getRefreshUrl()) {
        return Promise.resolve(null);
    }
    return doRefresh();
}

export function installRefreshInterceptor(instance) {
    installCrossTabTokenSync();

    instance.interceptors.response.use(
        (res) => res,
        (err) => {
            const response = err && err.response;
            const config = err && err.config;

            if (!response || response.status !== 401 || !config) {
                return Promise.reject(err);
            }

            // No refresh endpoint configured for this page (site-less) → nothing to do.
            const refreshUrl = getRefreshUrl();
            if (!refreshUrl) {
                return Promise.reject(err);
            }

            // Never refresh the refresh call itself, login calls, or requests marked to skip
            // refresh, and only retry a given request once.
            const url = config.url || "";
            if (
                config.skipRefresh ||
                url.indexOf(refreshUrl) !== -1 ||
                url.includes("/login") ||
                config._retriedAfterRefresh
            ) {
                return Promise.reject(err);
            }

            // No local session to refresh. On sites that require login, send them to login;
            // on sites that allow guest browsing (bo_require_login === false), leave them be so
            // they can keep browsing without an account. redirectToLogin() also no-ops on the
            // login page, so this can't loop.
            if (!isLoggedIn()) {
                if (window.bo_require_login !== false) {
                    reportUnexpectedAuthRedirect({
                        source: "protected_401_no_local_token",
                        reasonCode: "missing_local_token",
                        failedRequestUrl: config.url,
                        httpStatus: 401,
                        token: requestToken(config),
                    });
                    redirectToLogin();
                    return Promise.reject(err);
                }
                // Guest browsing is allowed, so the request itself is still answerable — it only
                // 401'd because a dead token was still pinned to the headers. Drop it and retry
                // as a guest rather than surfacing a failure the visitor cannot act on.
                config._retriedAfterRefresh = true;
                return retryAsGuest(instance, config, err);
            }
            // If another tab refreshed while this request was in flight, retry immediately
            // with its token instead of rotating the shared refresh cookie again.
            const rejectedToken = requestToken(config);
            const latestToken = readStoredToken();
            if (latestToken && rejectedToken && latestToken !== rejectedToken) {
                return retryWithToken(instance, config, latestToken);
            }
            config._retriedAfterRefresh = true;

            // NOTE: use .then(onFulfilled, onRejected) — NOT .then().catch(). The rejection
            // handler must fire ONLY when the refresh itself fails, never when the retried
            // request fails. If the retry fails for its own reasons (validation, server error,
            // a second 401 from a permission check), that error must propagate to the caller so
            // the real failure surfaces — it must not be misread as an expired session and
            // bounce the user to login.
            return doRefresh(rejectedToken || latestToken).then(
                (token) => {
                    return retryWithToken(instance, config, token);
                },
                (refreshErr) => {
                    // A refresh 401 definitively ends the session (expired/revoked cookie).
                    // A 429, network error, or 5xx is temporary: surface that request failure but
                    // preserve shared authentication so one overloaded tab cannot log out all
                    // other tabs.
                    if (!isDefinitiveRefreshFailure(refreshErr)) {
                        return Promise.reject(refreshErr);
                    }

                    const refreshErrors = refreshErrorDetails(refreshErr);
                    reportUnexpectedAuthRedirect({
                        eventId: refreshErrors.event_id || refreshErr.boAuthEventId,
                        source: "refresh_401",
                        reasonCode: refreshErrors.reason_code || "missing_cookie",
                        failedRequestUrl: config.url,
                        httpStatus: 401,
                        token: rejectedToken || latestToken,
                    });

                    clearAuthToken();
                    if (window.bo_require_login !== false) {
                        redirectToLogin();
                        return Promise.reject(refreshErr);
                    }
                    return retryAsGuest(instance, config, refreshErr);
                }
            );
        }
    );
}
